if (!customElements.get('gs-occasion-videos')) {
  class GsOccasionVideos extends HTMLElement {
    connectedCallback() {
      if (this.dataset.gsVideoCarouselReady === 'true') return;
      this.dataset.gsVideoCarouselReady = 'true';

      this.track = this.querySelector('[data-gs-video-track]');
      this.previousButton = this.querySelector('[data-gs-video-previous]');
      this.nextButton = this.querySelector('[data-gs-video-next]');
      this.progress = this.querySelector('[data-gs-video-progress]');
      this.videos = [...this.querySelectorAll('video')];
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.scrollFrame = undefined;

      if (!this.track) return;

      this.previousButton?.addEventListener('click', () => this.scrollCards(-1));
      this.nextButton?.addEventListener('click', () => this.scrollCards(1));
      this.track.addEventListener('scroll', () => this.requestUpdate(), { passive: true });
      this.track.addEventListener('keydown', (event) => this.handleKeyboard(event));
      this.track.addEventListener('play', (event) => this.pauseOtherVideos(event.target), true);
      this.onVisibilityChange = () => {
        if (document.hidden) this.videos.forEach((video) => video.pause());
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.requestUpdate());
        this.resizeObserver.observe(this.track);
      } else {
        this.onWindowResize = () => this.requestUpdate();
        window.addEventListener('resize', this.onWindowResize, { passive: true });
      }

      if (this.dataset.autoplay === 'true' && !this.reducedMotion.matches) {
        this.initializeAutoplay();
      }

      this.updateState();
    }

    disconnectedCallback() {
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      this.resizeObserver?.disconnect();
      this.autoplayObserver?.disconnect();
      if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
      if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize);
    }

    getScrollDistance() {
      const cards = [...this.track.children];
      if (cards.length > 1) return cards[1].offsetLeft - cards[0].offsetLeft;
      return cards[0]?.offsetWidth || this.track.clientWidth * 0.82;
    }

    scrollCards(direction) {
      this.track.scrollBy({
        left: this.getScrollDistance() * direction,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    handleKeyboard(event) {
      if (event.target !== this.track) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.scrollCards(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.scrollCards(-1);
      }
    }

    requestUpdate() {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = undefined;
        this.updateState();
      });
    }

    updateState() {
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const position = Math.min(maximum, Math.max(0, this.track.scrollLeft));
      const atStart = position <= 2;
      const atEnd = maximum <= 2 || position >= maximum - 2;

      if (this.previousButton) this.previousButton.disabled = atStart;
      if (this.nextButton) this.nextButton.disabled = atEnd;
      if (this.progress) this.progress.style.transform = `scaleX(${maximum ? position / maximum : 1})`;
    }

    pauseOtherVideos(activeVideo) {
      if (!(activeVideo instanceof HTMLVideoElement)) return;
      this.videos.forEach((video) => {
        if (video !== activeVideo && !video.paused) video.pause();
      });
    }

    initializeAutoplay() {
      if (!('IntersectionObserver' in window) || this.videos.length === 0) return;

      this.autoplayObserver = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.68)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        entries.forEach((entry) => {
          if (entry.intersectionRatio < 0.42 && !entry.target.paused) entry.target.pause();
        });

        if (visible && !document.hidden) {
          this.pauseOtherVideos(visible.target);
          visible.target.muted = true;
          visible.target.play().catch(() => {});
        }
      }, { root: this.track, threshold: [0, 0.42, 0.68, 1] });

      this.videos.forEach((video) => this.autoplayObserver.observe(video));
    }
  }

  customElements.define('gs-occasion-videos', GsOccasionVideos);
}
